import { z } from 'zod';

/**
 * Authentification applicative.
 *
 * Le cahier des charges prévoyait Cloudflare Access et aucun code
 * d'authentification. Access exige un domaine rattaché ; à défaut, cet écran
 * de connexion prend le relais. Dès que `ACCESS_TEAM` / `ACCESS_AUD` sont
 * renseignés, Access redevient prioritaire et ceci n'est plus sollicité.
 */

export const connexionSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "L'adresse e-mail est obligatoire")
    .email('Adresse e-mail invalide')
    .transform((v) => v.toLowerCase()),
  mot_de_passe: z.string().min(1, 'Le mot de passe est obligatoire'),
});
export type ConnexionInput = z.infer<typeof connexionSchema>;

/**
 * Douze caractères minimum : ce compte protège un fichier client complet
 * (noms, téléphones, corrections) exposé sur Internet.
 */
export const MOT_DE_PASSE_MIN = 12;

export const motDePasseSchema = z
  .string()
  .min(MOT_DE_PASSE_MIN, `Le mot de passe doit faire au moins ${MOT_DE_PASSE_MIN} caractères`)
  .max(200, 'Le mot de passe est trop long');

export const changerMotDePasseSchema = z
  .object({
    actuel: z.string().min(1, 'Le mot de passe actuel est obligatoire'),
    nouveau: motDePasseSchema,
    confirmation: z.string(),
  })
  .refine((v) => v.nouveau === v.confirmation, {
    message: 'Les deux mots de passe ne correspondent pas',
    path: ['confirmation'],
  })
  .refine((v) => v.nouveau !== v.actuel, {
    message: 'Le nouveau mot de passe doit être différent de l’actuel',
    path: ['nouveau'],
  });
export type ChangerMotDePasseInput = z.infer<typeof changerMotDePasseSchema>;

/**
 * Comment le serveur authentifie ses appels.
 *
 * - `access` — Cloudflare Access est configuré : il affiche son propre écran,
 *   l'application ne doit surtout pas en proposer un second.
 * - `session` — connexion applicative par mot de passe.
 * - `ouvert` — aucun contrôle (`DEV_SKIP_AUTH`), développement local seulement.
 */
export type ModeAuth = 'access' | 'session' | 'ouvert';

export type EtatAuth = {
  mode: ModeAuth;
  /**
   * Faux quand aucun compte n'existe encore : le formulaire refuserait tout,
   * autant afficher la marche à suivre. Toujours vrai hors du mode `session`.
   */
  configure: boolean;
};

export type Utilisateur = {
  id: string;
  email: string;
  nom: string | null;
  derniere_connexion: string | null;
};

export type Connexion = {
  /** Jeton de session opaque, à présenter en `Authorization: Bearer`. */
  jeton: string;
  expire_at: string;
  utilisateur: Utilisateur;
};

/** Durée d'une session. Au-delà, il faut se reconnecter. */
export const DUREE_SESSION_HEURES = 12;

/**
 * Force indicative du mot de passe, pour guider la saisie.
 * Purement consultatif : la seule règle imposée est la longueur minimale.
 */
export function forceMotDePasse(motDePasse: string): {
  score: 0 | 1 | 2 | 3 | 4;
  libelle: string;
} {
  let score = 0;
  if (motDePasse.length >= MOT_DE_PASSE_MIN) score += 1;
  if (motDePasse.length >= 16) score += 1;
  if (/[a-z]/.test(motDePasse) && /[A-Z]/.test(motDePasse)) score += 1;
  if (/\d/.test(motDePasse) && /[^A-Za-z0-9]/.test(motDePasse)) score += 1;

  const libelles = ['Très faible', 'Faible', 'Correct', 'Bon', 'Excellent'] as const;
  const borne = Math.min(score, 4) as 0 | 1 | 2 | 3 | 4;
  return { score: borne, libelle: libelles[borne] };
}
